/* oxlint-disable max-lines */
import { Operation, TemplateVersion } from '@omnea-digital/extops-schema'
import { TFunction } from 'i18next'
import { FormTypeEnum } from '@omnea-app/packages/universal/form-templates/formTemplateEnums'
import { FormConfig } from '@omnea-app/packages/universal/formBuilder/form'
import { GlobalIntegration, globalIntegrationsMetadata } from '@omnea-app/packages/universal/integrations/globalIntegrations'
import { DEFAULT_INHERENT_RISK_AGENT_CONFIG } from '@omnea-app/packages/universal/risks/inherentRiskInstructions'
import { RISK_CONTROLS_AGENT_FILL_IN_CONFIG } from '@omnea-app/packages/universal/risks/riskControlsAgent/riskControlsAgentInstructions'
import { RequestStepTaskTypeEnum } from '@omnea-app/packages/universal/tasks/taskEnums'
import { assertNever } from '@omnea-app/packages/universal/type-guards/assertNever'
import { propertyIsDefined } from '@omnea-app/packages/universal/type-guards/propertyIsDefined'
import { AssigneeTypeEnum } from '@omnea-app/packages/universal/users-and-groups/assigneeType'
import { AssessmentType, rfxSupplierSources } from '@omnea-app/packages/universal/workflow/blockConfig'
import { BlockDefaultLabel } from '@omnea-app/packages/universal/workflow/blockDefaultLabel'
import { BlockTypeEnum } from '@omnea-app/packages/universal/workflow/blockType'
import { WORKING_HOURS_PER_DAY } from '@omnea-app/packages/universal/workflow/escalationConstants'
import { isLinkedBlockConfig, type LinkedBlockConfig } from '@omnea-app/packages/universal/workflow/linkedBlockConfig'
import {
  normaliseSubWorkflowAttachments,
  toSubWorkflowIds,
  type SubWorkflowAttachment,
} from '@omnea-app/packages/universal/workflow/subWorkflowAttachment'
import { requestorAssignee } from '@omnea-app/packages/universal/workflow/variableAssigneeTypes'
import {
  defaultAuctionNotificationMessage,
  defaultBudgetApprovalNotificationMessage,
  defaultCancelAgreementNotificationMessage,
  defaultCancelAssessmentsManualNotificationMessage,
  defaultCommitSupplierDataNotificationMessage,
  defaultCreateAgreementNotificationMessage,
  defaultCreateAssessmentNotificationMessage,
  defaultCreatePONotificationMessage,
  defaultEngagementBlockNotificationMessage,
  defaultInherentRiskAssessmentNotificationMessage,
  defaultNotifyOnlyNotificationMessage,
  defaultRejectRequestNotificationMessage,
  defaultChannelNotificationMessage,
  defaultRfxNotificationMessage,
  defaultSupplierPortalNotificationMessage,
  defaultTaskNotificationMessage,
} from './defaultNotificationMessage'
import type { CustomIntegration } from '@omnea-app/packages/universal/extopsApiClient/integrationTypes'
import type { ProcedureTemplate } from '@omnea-app/packages/universal/extopsApiClient/procedureTypes'
import type { AssigneeEntity } from '@omnea-app/packages/universal/workflow/assigneeEntity'
import type {
  ApprovalBlockConfig,
  AuctionBlockConfig,
  BlockConfig,
  BudgetApprovalBlockConfig,
  BudgetApprovalNotificationMessage,
  CancelAgreementBlockConfig,
  CancelAssessmentsBlockConfig,
  CommitSupplierDataBlockConfig,
  CreateAgreementBlockConfig,
  CreateAssessmentBlockConfig,
  CreatePOBlockConfig,
  CreateRenewalBlockConfig,
  DataTransformationBlockConfig,
  DefaultConfig,
  EngagementBlockConfig,
  ExternalOperationBlockConfig,
  ExternalOperationSubType,
  ExternalProcedureBlockConfig,
  HttpBlockConfig,
  HttpHeader,
  InherentRiskAssessmentBlockConfig,
  IntakeBlockConfig,
  NotificationAttachment,
  NotificationBlockConfig,
  NotificationMessage,
  NotifySlackBlockConfig,
  NotifyTeamsBlockConfig,
  PriceBenchmarkBlockConfig,
  RejectRequestBlockConfig,
  RenewalAssessmentBlockConfig,
  RenewalWorkflowConfig,
  RequestNamingMode,
  RfxBlockConfig,
  RfxEvaluation,
  RfxEvaluationCriterion,
  RfxSupplierFilters,
  RfxSupplierSource,
  SharedAuctionSettings,
  Skip,
  SupplierOnboardingBlockConfig,
  SupplierOnlineResourcesBlockConfig,
  TargetStartDateType,
  WaitingBlockConfig,
} from '@omnea-app/packages/universal/workflow/blockConfig'
import type { GraphNode, NodeCustomAttributes } from '@omnea-app/packages/universal/workflow/graphModel'
import type { FillInFormTask } from '@omnea-app/packages/universal/workflow/taskTypes'

export { BlockDefaultLabel, BlockTypeEnum }
export { AssessmentType, rfxSupplierSources }
export type {
  ApprovalBlockConfig,
  AuctionBlockConfig,
  BlockConfig,
  BudgetApprovalBlockConfig,
  BudgetApprovalNotificationMessage,
  CancelAgreementBlockConfig,
  CancelAssessmentsBlockConfig,
  CommitSupplierDataBlockConfig,
  CreateAgreementBlockConfig,
  CreateAssessmentBlockConfig,
  CreatePOBlockConfig,
  CreateRenewalBlockConfig,
  DataTransformationBlockConfig,
  EngagementBlockConfig,
  ExternalOperationBlockConfig,
  ExternalOperationSubType,
  ExternalProcedureBlockConfig,
  HttpBlockConfig,
  HttpHeader,
  InherentRiskAssessmentBlockConfig,
  IntakeBlockConfig,
  NotificationAttachment,
  NotificationBlockConfig,
  NotificationMessage,
  NotifySlackBlockConfig,
  NotifyTeamsBlockConfig,
  PriceBenchmarkBlockConfig,
  RejectRequestBlockConfig,
  RenewalAssessmentBlockConfig,
  RenewalWorkflowConfig,
  RequestNamingMode,
  RfxBlockConfig,
  RfxEvaluation,
  RfxEvaluationCriterion,
  RfxSupplierFilters,
  RfxSupplierSource,
  SharedAuctionSettings,
  Skip,
  SupplierOnboardingBlockConfig,
  SupplierOnlineResourcesBlockConfig,
  TargetStartDateType,
  WaitingBlockConfig,
}

export const NON_RENAMEABLE_BLOCK_TYPES: ReadonlySet<BlockTypeEnum> = new Set([
  BlockTypeEnum.MILESTONE,
  BlockTypeEnum.CHILD_REQUEST_START,
  BlockTypeEnum.SUB_WORKFLOW_START,
])

export type EngagementFormTemplate = {
  id: string
  config: FormConfig
  title: string
  type: FormTypeEnum
  description: string
}

export const getBlockTasks = (block: GraphNode<BlockConfig>, engagementFormTemplates: EngagementFormTemplate[]) => {
  const payload = block.metadata.config.payload

  if (block.metadata.type === BlockTypeEnum.ENGAGEMENT) {
    return {
      blockLabel: payload.label,
      tasks: engagementFormTemplates.map<FillInFormTask>((form) => ({
        formTemplateId: form.id,
        type: RequestStepTaskTypeEnum.FILL_IN,
      })),
    }
  }

  if (propertyIsDefined(payload, 'tasks')) {
    return {
      canvasModelId: block.id,
      blockLabel: payload.label,
      tasks: payload.tasks,
    }
  }

  return undefined
}

export type { LinkedBlockConfig }
export { isLinkedBlockConfig }

// DATA TRANSFORMATION
export type {
  TransformationValue,
  TransformationArg,
  TransformationRule,
  ManagerWalkStart,
  SubjectPredicate,
  SwitchBranch,
  VariableReference,
  TransformationOutputField,
  TransformationOutputPrimitive,
} from '@omnea-app/packages/universal/workflow/dataTransformation'

export { AssigneeEntitySchema } from '@omnea-app/packages/universal/workflow/assigneeEntity'
export type { AssigneeEntity } from '@omnea-app/packages/universal/workflow/assigneeEntity'

// These are variables we support for providing emails as assignees instead of users.
// that can then be used in the notification block to send notifications
export type EmailAssigneeVariable = 'supplierPortalAssignees' | 'primaryContactEmail'

export type {
  ReviewTask,
  FillInFormTask,
  EsignFillInTask,
  FillInOrReviewTask,
  EsignTask,
  SummaryApprovalTask,
  ContentGenerationTask,
  BlockTask,
} from '@omnea-app/packages/universal/workflow/taskTypes'
export type TaskForm = { id: string; title: string; type: FormTypeEnum }

export const DEFAULT_BLOCK_TARGET_TIME_VALUE = WORKING_HOURS_PER_DAY

export const isBudgetApprovalNotificationMessage = (
  notificationMessage: NotificationMessage | BudgetApprovalNotificationMessage
): notificationMessage is BudgetApprovalNotificationMessage => {
  return 'approve' in notificationMessage && 'notify' in notificationMessage
}

export const defaultSharedAuctionSettings: SharedAuctionSettings = {
  currency: 'GBP',
  targetTimeValue: null,
  showLotRank: false,
  showLowestPricePerLot: false,
  showStartingPrice: false,
  notificationMessage: defaultAuctionNotificationMessage,
}

export const getBlockSubWorkflowAttachments = (payload: BlockConfig['payload']): SubWorkflowAttachment[] =>
  'subWorkflowIds' in payload || 'subWorkflows' in payload ? normaliseSubWorkflowAttachments(payload) : []

export const getBlockSubWorkflowIds = (payload: BlockConfig['payload']): string[] =>
  toSubWorkflowIds(getBlockSubWorkflowAttachments(payload))

export const resolveTargetStartDateType = (payload: CreateAssessmentBlockConfig['payload']): TargetStartDateType => {
  if (payload.targetStartDateType) return payload.targetStartDateType
  return payload.shouldUseExactTargetStartDate ? 'exact' : 'relative'
}

const getExternalOperationBlockConfig = (node: ExternalOperationNode) => ({
  type: BlockTypeEnum.EXTERNAL_OPERATION,
  validationErrors: [],
  config: {
    payload: {
      subType: node.subType,
      label:
        globalIntegrationsMetadata[node.integration].blockLabel ??
        `${globalIntegrationsMetadata[node.integration].title} - ${node.operationName}`,
      iconSrc: globalIntegrationsMetadata[node.integration].logo,
      isCustomAccountingIntegration: node.integration === 'albertsons', // only non-custom integration supporting line item custom fields
      operation: node.operation,
      integration: node.integration,
      targetTimeValue: DEFAULT_BLOCK_TARGET_TIME_VALUE,
      assignees: [],
      ...(node.hideFromTimeline && { hideFromTimeline: node.hideFromTimeline }),
      ...(node.continueOnFailure && { continueOnFailure: node.continueOnFailure }),
    },
  } satisfies ExternalOperationBlockConfig,
})

const getCustomExternalOperationBlockConfig = (customIntegration: CustomIntegration) => {
  const basePayload = {
    label: `${customIntegration.name}`,
    customIntegrationId: customIntegration.customIntegrationId,
    iconFilestoreId: customIntegration.logo,
    targetTimeValue: DEFAULT_BLOCK_TARGET_TIME_VALUE,
    assignees: [],
  }
  switch (customIntegration.type) {
    case 'http':
      return {
        type: BlockTypeEnum.EXTERNAL_OPERATION,
        validationErrors: [],
        config: {
          payload: {
            subType: 'HTTP-CUSTOM-INTEGRATION',
            isCustomAccountingIntegration: customIntegration.isAccountingIntegration ?? false,
            operation: 'http-custom-integration',
            integration: 'http',
            ...basePayload,
          },
        } satisfies ExternalOperationBlockConfig,
      }
    case 'sftp':
      return {
        type: BlockTypeEnum.EXTERNAL_OPERATION,
        validationErrors: [],
        config: {
          payload: {
            subType: 'SFTP-UPLOAD-FILE',
            operation: 'sftp-upload-file',
            integration: 'sftp',
            ...basePayload,
          },
        } satisfies ExternalOperationBlockConfig,
      }
    default:
      assertNever(customIntegration)
  }
}

// TODO EXP-1388 load operations from extops service instead of hardcoding them
export const externalOperations: Record<ExternalOperationSubType, ExternalOperationNode> = {
  'ALBERTSONS-CONVERT-TO-CSV': {
    integration: 'albertsons',
    subType: 'ALBERTSONS-CONVERT-TO-CSV',
    operationName: 'Convert to CSV',
    operation: 'albertsons-convert-to-csv',
    description: 'Convert request data to CSV',
  },
  'ADOBE-SIGN-CREATE-AGREEMENT': {
    integration: 'adobe-sign',
    subType: 'ADOBE-SIGN-CREATE-AGREEMENT',
    operationName: 'Create Agreement (eSign)',
    operation: 'adobe-sign-create-agreement',
    description: 'Create an agreement in Adobe Sign and wait for it to be signed',
  },

  'CREDITSAFE-GENERATE_REPORT': {
    integration: 'creditsafe',
    subType: 'CREDITSAFE-GENERATE_REPORT',
    operationName: 'Generate Report',
    operation: 'creditsafe-generate-report',
    description: 'Create a credit report in Credit Safe',
  },
  'DOCUSIGN-DOCUMENT-UPLOAD': {
    integration: 'docusign',
    subType: 'DOCUSIGN-DOCUMENT-UPLOAD',
    operationName: 'Document Upload (CLM)',
    operation: 'docusign-upload-document',
    description: 'Upload a document to DocuSign and wait for it to be signed',
  },
  'DOCUSIGN-CLM-UPLOAD-DOCUMENT': {
    integration: 'docusign-clm',
    subType: 'DOCUSIGN-CLM-UPLOAD-DOCUMENT',
    operationName: 'Upload Document',
    operation: 'docusign-clm-upload-document',
    description:
      'Upload a document to DocuSign CLM and either pull the uploaded document immediately or await external status before pulling',
  },
  'DOCUSIGN-ESIGN-CREATE-ENVELOPE': {
    integration: 'docusign',
    subType: 'DOCUSIGN-ESIGN-CREATE-ENVELOPE',
    operationName: 'Create Envelope (eSign)',
    operation: 'docusign-esign-create-envelope',
    description: 'Create an envelope with a contract in DocuSign eSign and wait for it to be signed',
  },
  'IRONCLAD-COMPLETE-WORKFLOW': {
    integration: 'ironclad',
    subType: 'IRONCLAD-COMPLETE-WORKFLOW',
    operationName: 'Run Workflow End-to-End',
    operation: 'ironclad-complete-workflow',
    description: 'Create an Ironclad workflow and wait for it to complete',
  },
  'IRONCLAD-INITIATE-WORKFLOW': {
    integration: 'ironclad',
    subType: 'IRONCLAD-INITIATE-WORKFLOW',
    operationName: 'Initiate Workflow',
    operation: 'ironclad-initiate-workflow',
    description: 'Initiate an Ironclad workflow',
  },
  'IRONCLAD-WAIT-FOR-COMPLETION': {
    integration: 'ironclad',
    subType: 'IRONCLAD-WAIT-FOR-COMPLETION',
    operationName: 'Wait for Completion',
    operation: 'ironclad-wait-for-completion',
    description: 'Wait for an Ironclad workflow to complete',
  },
  'IRONCLAD-UPDATE-WORKFLOW-APPROVAL': {
    integration: 'ironclad',
    subType: 'IRONCLAD-UPDATE-WORKFLOW-APPROVAL',
    operationName: 'Update Workflow Approval',
    operation: 'ironclad-update-workflow-approval',
    description: 'Update a workflow approval in Ironclad',
  },
  'IRONCLAD-GET-DOCUMENT': {
    integration: 'ironclad',
    subType: 'IRONCLAD-GET-DOCUMENT',
    operationName: 'Get Document',
    operation: 'ironclad-get-document',
    description: 'Get a document from Ironclad',
  },
  'PROCESS-UNITY-IMPORT-EXPORT': {
    integration: 'process-unity',
    subType: 'PROCESS-UNITY-IMPORT-EXPORT',
    operationName: 'Import/Export',
    operation: 'process-unity-import-export',
    description: 'Import an assessment into Process Unity and wait for a report to be completed',
  },
  'TPRM-CONTINUOUS-MONITORING-POINT-IN-TIME-CHECK': {
    integration: 'tprm-continuous-monitoring',
    subType: 'TPRM-CONTINUOUS-MONITORING-POINT-IN-TIME-CHECK',
    operationName: 'Risk Screening',
    operation: 'tprm-continuous-monitoring-point-in-time-check',
    description: 'Scan suppliers for third party risk as you onboard them',
  },
  'THOMSON-REUTERS-GENERATE-REPORT': {
    integration: 'thomson-reuters',
    subType: 'THOMSON-REUTERS-GENERATE-REPORT',
    operationName: 'Generate Report',
    operation: 'thomson-reuters-generate-report',
    description: 'Create a risk report in Thomson Reuters',
  },
  'JIRA-CREATE-TICKET': {
    integration: 'jira',
    subType: 'JIRA-CREATE-TICKET',
    operationName: 'Create Ticket',
    operation: 'jira-create-ticket',
    description: 'Create a ticket in Jira and wait for it to complete',
  },
  'JIRA-SERVICE-MANAGEMENT-CREATE-TICKET': {
    integration: 'jira-service-management',
    subType: 'JIRA-SERVICE-MANAGEMENT-CREATE-TICKET',
    operationName: 'Create Ticket',
    operation: 'jira-service-management-create-ticket',
    description: 'Create a ticket in Jira Service Management and wait for it to complete',
  },
  'MESH-CREATE-VIRTUAL-CARD': {
    integration: 'mesh',
    subType: 'MESH-CREATE-VIRTUAL-CARD',
    operationName: 'Create Virtual Card',
    operation: 'mesh-create-virtual-card',
    description: 'Create a virtual card in Mesh Payments',
  },
  'NOOP-TEST': {
    integration: 'noop',
    subType: 'NOOP-TEST',
    operationName: 'Test',
    operation: 'noop',
    description: 'No operation (for testing purposes)',
  },
  'ONE-TRUST-CREATE-ASSESSMENT': {
    integration: 'onetrust',
    subType: 'ONE-TRUST-CREATE-ASSESSMENT',
    operationName: 'Create Assessment',
    operation: 'onetrust-create-assessment',
    description: 'Create a One Trust assessment',
  },
  'ONE-TRUST-CREATE-VENDOR': {
    integration: 'onetrust',
    subType: 'ONE-TRUST-CREATE-VENDOR',
    operationName: 'Create Vendor',
    operation: 'onetrust-create-vendor',
    description: 'Create a One Trust vendor',
  },
  'PANDADOC-CREATE-AGREEMENT': {
    integration: 'pandadoc',
    subType: 'PANDADOC-CREATE-AGREEMENT',
    operationName: 'Create Agreement',
    operation: 'pandadoc-create-agreement',
    description: 'Create an agreement in PandaDoc and wait for it to be signed',
  },
  'ZAPIER-ZAP': {
    integration: 'zapier',
    subType: 'ZAPIER-ZAP',
    operationName: 'Trigger Integration',
    operation: 'zapier-execute-zap',
    description: 'Execute an integration via a Zapier zap',
  },
  'ZENDESK-CREATE-TICKET': {
    integration: 'zendesk',
    subType: 'ZENDESK-CREATE-TICKET',
    operationName: 'Create Ticket',
    operation: 'zendesk-create-ticket',
    description: 'Create a ticket in Zendesk',
  },
  'COUPA-CLM-CREATE-CONTRACT': {
    integration: 'coupa-clm',
    subType: 'COUPA-CLM-CREATE-CONTRACT',
    operationName: 'Create Contract',
    operation: 'coupa-clm-contract',
    description: 'Create a contract and upload metadata to Coupa CLM',
  },
  'TRULIOO-TRIGGER-WORKFLOW': {
    integration: 'trulioo',
    subType: 'TRULIOO-TRIGGER-WORKFLOW',
    operationName: 'Trigger workflow',
    operation: 'trulioo-trigger-workflow',
    description: 'Trigger a Trulioo workflow',
  },
  'HTTP-CUSTOM-INTEGRATION': {
    integration: 'http',
    subType: 'HTTP-CUSTOM-INTEGRATION',
    operationName: 'HTTP Custom Integration',
    operation: 'http-custom-integration',
    description: 'Execute a custom HTTP integration',
  },
  'SFTP-UPLOAD-FILE': {
    integration: 'sftp',
    subType: 'SFTP-UPLOAD-FILE',
    operationName: 'Upload File via SFTP',
    operation: 'sftp-upload-file',
    description: 'Upload a file to an SFTP server',
  },
  'CONTRACTPODAI-CREATE-CONTRACT': {
    integration: 'contractpodai',
    subType: 'CONTRACTPODAI-CREATE-CONTRACT',
    operationName: 'Create Contract',
    operation: 'contractpodai-create-contract',
    description: 'Create a contract in ContractPodAI',
  },
  'GOOGLE-DRIVE-UPLOAD-FILE': {
    integration: 'google-drive',
    subType: 'GOOGLE-DRIVE-UPLOAD-FILE',
    operationName: 'Upload File',
    operation: 'google-drive-upload-file',
    description: 'Upload a file to Google Drive',
  },
  'LINKSQUARES-CREATE-AGREEMENT': {
    integration: 'linksquares',
    subType: 'LINKSQUARES-CREATE-AGREEMENT',
    operationName: 'Create Agreement',
    operation: 'linksquares-create-agreement',
    description: 'Create an agreement in LinkSquares',
  },
  'LINKSQUARES-APPROVE-TASK': {
    integration: 'linksquares',
    subType: 'LINKSQUARES-APPROVE-TASK',
    operationName: 'Approve Task',
    operation: 'linksquares-approve-task',
    description: 'Approve an agreement task in LinkSquares',
  },
  'LINKSQUARES-PULL-COMPLETED-AGREEMENT': {
    integration: 'linksquares',
    subType: 'LINKSQUARES-PULL-COMPLETED-AGREEMENT',
    operationName: 'Pull Completed Agreement',
    operation: 'linksquares-pull-completed-agreement',
    description: 'Pull a completed agreement from LinkSquares',
  },
  'LINKSQUARES-CREATE-AND-PULL-AGREEMENT': {
    integration: 'linksquares',
    subType: 'LINKSQUARES-CREATE-AND-PULL-AGREEMENT',
    operationName: 'Create and Pull Agreement',
    operation: 'linksquares-create-and-pull-agreement',
    description: 'Create an agreement, wait for it to be signed and pull it from LinkSquares',
  },
  'CONGA-CONTRACTS-CREATE-CONTRACT': {
    integration: 'conga-contracts',
    subType: 'CONGA-CONTRACTS-CREATE-CONTRACT',
    operationName: 'Create Contract',
    operation: 'conga-contracts-create-contract',
    description: 'Create a contract in Conga Contracts',
  },
  'PUNCHOUT-GENERIC-CREATE-ORDER-REQUEST': {
    integration: 'punchout-generic',
    subType: 'PUNCHOUT-GENERIC-CREATE-ORDER-REQUEST',
    operationName: 'Create Order',
    operation: 'punchout-generic-create-order-request',
    description: 'Create a purchase order request via punchout',
  },
  'PUNCHOUT-AMAZON-BUSINESS-CREATE-ORDER-REQUEST': {
    integration: 'punchout-amazon-business',
    subType: 'PUNCHOUT-AMAZON-BUSINESS-CREATE-ORDER-REQUEST',
    operationName: 'Create Order',
    operation: 'punchout-amazon-business-create-order-request',
    description: 'Create a purchase order request via Amazon Business punchout',
  },
  'PUNCHOUT-UNITE-CREATE-ORDER-REQUEST': {
    integration: 'punchout-unite',
    subType: 'PUNCHOUT-UNITE-CREATE-ORDER-REQUEST',
    operationName: 'Create Order',
    operation: 'punchout-unite-create-order-request',
    description: 'Create a purchase order request via Unite punchout',
  },
  'PUNCHOUT-CDW-CREATE-ORDER-REQUEST': {
    integration: 'punchout-cdw',
    subType: 'PUNCHOUT-CDW-CREATE-ORDER-REQUEST',
    operationName: 'Create Order',
    operation: 'punchout-cdw-create-order-request',
    description: 'Create a purchase order request via CDW punchout',
  },
  'PUNCHOUT-GRAINGER-CREATE-ORDER-REQUEST': {
    integration: 'punchout-grainger',
    subType: 'PUNCHOUT-GRAINGER-CREATE-ORDER-REQUEST',
    operationName: 'Create Order',
    operation: 'punchout-grainger-create-order-request',
    description: 'Create a purchase order request via Grainger punchout',
  },
  'PUNCHOUT-QUILL-CREATE-ORDER-REQUEST': {
    integration: 'punchout-quill',
    subType: 'PUNCHOUT-QUILL-CREATE-ORDER-REQUEST',
    operationName: 'Create Order',
    operation: 'punchout-quill-create-order-request',
    description: 'Create a purchase order request via Quill punchout',
  },
  'PUNCHOUT-STAPLES-CREATE-ORDER-REQUEST': {
    integration: 'punchout-staples',
    subType: 'PUNCHOUT-STAPLES-CREATE-ORDER-REQUEST',
    operationName: 'Create Order',
    operation: 'punchout-staples-create-order-request',
    description: 'Create a purchase order request via Staples punchout',
  },
  'PUNCHOUT-ROBERTSON-MARKETING-CREATE-ORDER-REQUEST': {
    integration: 'punchout-robertson-marketing',
    subType: 'PUNCHOUT-ROBERTSON-MARKETING-CREATE-ORDER-REQUEST',
    operationName: 'Create Order',
    operation: 'punchout-robertson-marketing-create-order-request',
    description: 'Create a purchase order request via Robertson Marketing punchout',
  },
  'TROPIC-PRICE-BENCHMARK': {
    integration: 'tropic',
    subType: 'TROPIC-PRICE-BENCHMARK',
    operationName: 'Price Intelligence',
    operation: 'tropic-price-benchmark',
    description: 'Run price intelligence analysis on a supplier quote',
  },
  'TROPIC-CREATE-CONTRACT': {
    integration: 'tropic',
    subType: 'TROPIC-CREATE-CONTRACT',
    operationName: 'Anonymised Contract Data',
    operation: 'tropic-create-contract',
    description: 'Send the signed contract to Tropic, where its pricing data is anonymised into the price benchmark dataset',
    hideFromTimeline: true,
    continueOnFailure: true,
  },
} as const

export type ExternalOperationNode = {
  integration: GlobalIntegration
  subType: ExternalOperationSubType
  operationName: string
  operation: Operation
  description: string
  hideFromTimeline?: boolean
  continueOnFailure?: boolean
}

export const getExternalOperationNodeNavigation = (nodes: ExternalOperationNode[]) => {
  return {
    name: 'External operations',
    children: nodes.map((node) => ({
      name:
        globalIntegrationsMetadata[node.integration].menuLabel ??
        `${globalIntegrationsMetadata[node.integration].title} - ${node.operationName}`,
      defaultConfig: getExternalOperationBlockConfig(node),
      type: BlockTypeEnum.EXTERNAL_OPERATION,
      subType: node.subType,
    })),
  }
}

export const getCustomExternalOperationNodeNavigation = (customIntegrations: CustomIntegration[]) => {
  return {
    name: 'Custom integrations',
    children: customIntegrations.map((customIntegration) => ({
      name: `Custom - ${customIntegration.name}`,
      customIntegration,
      defaultConfig: getCustomExternalOperationBlockConfig(customIntegration),
      type: BlockTypeEnum.EXTERNAL_OPERATION,
      subType: 'HTTP-CUSTOM-INTEGRATION',
    })),
  }
}

export const getProcedureBlockConfig = (
  customIntegration: CustomIntegration,
  template: Pick<ProcedureTemplate, 'id' | 'name'> & { version?: TemplateVersion }
) => ({
  type: BlockTypeEnum.EXTERNAL_PROCEDURE,
  validationErrors: [],
  config: {
    payload: {
      label: `${customIntegration.name} - ${template.name}`,
      procedureTemplateId: template.id,
      templateVersion: template.version,
      customIntegrationId: customIntegration.customIntegrationId,
      integration: customIntegration.type,
      isCustomAccountingIntegration:
        customIntegration.type === 'http' ? (customIntegration.isAccountingIntegration ?? false) : false,
      iconFilestoreId: customIntegration.logo,
      targetTimeValue: DEFAULT_BLOCK_TARGET_TIME_VALUE,
      assignees: [],
    },
  } satisfies ExternalProcedureBlockConfig,
})

export const getBudgetApprovalNodeNavigation = (budgetApprovalGroups: { id: string; name: string }[]) => {
  const config = BlockDefaultConfig[BlockTypeEnum.BUDGET_APPROVAL]
  return {
    name: 'Budget approvals',
    children: budgetApprovalGroups.map((group) => ({
      name: group.name,
      defaultConfig: {
        ...config,
        config: {
          ...config.config,
          payload: {
            ...config.config.payload,
            label: group.name,
            budgetApprovalGroupId: group.id,
          },
        },
      },
      type: BlockTypeEnum.BUDGET_APPROVAL,
    })),
  }
}

// Block types whose `shouldCompleteAutomatically` flag exempts them from requiring a duration.
export const BlockSupportsAutoCompleteDurationExemption: Record<BlockTypeEnum, boolean> = {
  [BlockTypeEnum.CREATE_RENEWAL]: true,
  [BlockTypeEnum.CREATE_ASSESSMENT]: true,
  [BlockTypeEnum.APPROVAL]: false,
  [BlockTypeEnum.BUDGET_APPROVAL]: false,
  [BlockTypeEnum.CHILD_REQUEST_START]: false,
  [BlockTypeEnum.SUB_WORKFLOW_START]: false,
  [BlockTypeEnum.ENGAGEMENT]: false,
  [BlockTypeEnum.HTTP]: false,
  [BlockTypeEnum.INTAKE]: false,
  [BlockTypeEnum.MILESTONE]: false,
  [BlockTypeEnum.NOTIFICATION]: false,
  [BlockTypeEnum.RENEWAL_ASSESSMENT]: false,
  [BlockTypeEnum.SUPPLIER_ONBOARDING]: false,
  [BlockTypeEnum.EXTERNAL_OPERATION]: false,
  [BlockTypeEnum.EXTERNAL_PROCEDURE]: false,
  [BlockTypeEnum.COMMIT_SUPPLIER_DATA]: false,
  [BlockTypeEnum.CREATE_AGREEMENT]: false,
  [BlockTypeEnum.CANCEL_AGREEMENT]: true,
  [BlockTypeEnum.CANCEL_ASSESSMENTS]: true,
  [BlockTypeEnum.CREATE_PO]: false,
  [BlockTypeEnum.RFX]: false,
  [BlockTypeEnum.AUCTION]: false,
  [BlockTypeEnum.PRICE_BENCHMARK]: false,
  [BlockTypeEnum.DATA_TRANSFORMATION]: false,
  [BlockTypeEnum.WAITING]: false,
  [BlockTypeEnum.SUPPLIER_ONLINE_RESOURCES]: false,
  [BlockTypeEnum.INHERENT_RISK_ASSESSMENT]: false,
  [BlockTypeEnum.RISK_CONTROLS_AGENT]: false,
  [BlockTypeEnum.NOTIFY_SLACK]: false,
  [BlockTypeEnum.REJECT_REQUEST]: false,
  [BlockTypeEnum.NOTIFY_TEAMS]: false,
}

export const BlockDefaultConfig: DefaultConfig = {
  [BlockTypeEnum.SUPPLIER_ONBOARDING]: {
    type: BlockTypeEnum.SUPPLIER_ONBOARDING,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.SUPPLIER_ONBOARDING],
        targetTimeValue: DEFAULT_BLOCK_TARGET_TIME_VALUE,
        notificationMessage: defaultSupplierPortalNotificationMessage,
        tasks: [],
        prefillFromSupplierRecord: true,
        prefillFromSupplierRecordOnSupplierProductMatch: false,
        assessmentType: AssessmentType.EXTERNAL,
        shouldCompleteAutomatically: false,
      },
    },
  },
  [BlockTypeEnum.APPROVAL]: {
    type: BlockTypeEnum.APPROVAL,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.APPROVAL],
        assignees: [],
        notificationMessage: defaultTaskNotificationMessage,
        targetTimeValue: DEFAULT_BLOCK_TARGET_TIME_VALUE,
        tasks: [],
        prefillFromSupplierRecord: false,
        prefillFromSupplierRecordOnSupplierProductMatch: false,
      },
    },
  },
  [BlockTypeEnum.MILESTONE]: {
    type: BlockTypeEnum.MILESTONE,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.MILESTONE],
      },
    },
  },
  [BlockTypeEnum.CHILD_REQUEST_START]: {
    type: BlockTypeEnum.CHILD_REQUEST_START,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.CHILD_REQUEST_START],
      },
    },
  },
  [BlockTypeEnum.SUB_WORKFLOW_START]: {
    type: BlockTypeEnum.SUB_WORKFLOW_START,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.SUB_WORKFLOW_START],
      },
    },
  },
  [BlockTypeEnum.NOTIFICATION]: {
    type: BlockTypeEnum.NOTIFICATION,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.NOTIFICATION],
        notificationMessage: defaultNotifyOnlyNotificationMessage,
        assignees: [],
        externalRecipientEmails: [],
      },
    },
  },
  [BlockTypeEnum.ENGAGEMENT]: {
    type: BlockTypeEnum.ENGAGEMENT,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.ENGAGEMENT],
        assignees: [],
        targetTimeValue: DEFAULT_BLOCK_TARGET_TIME_VALUE,
        notificationMessage: defaultEngagementBlockNotificationMessage,
      },
    },
  },
  [BlockTypeEnum.BUDGET_APPROVAL]: {
    type: BlockTypeEnum.BUDGET_APPROVAL,
    config: {
      payload: {
        label: '',
        targetTimeValue: DEFAULT_BLOCK_TARGET_TIME_VALUE,
        budgetApprovalGroupId: '',
        notificationMessage: defaultBudgetApprovalNotificationMessage,
        tasks: [],
        skipIfRequestor: false,
        skipIfAlreadyApproved: false,
      },
    },
  },
  [BlockTypeEnum.HTTP]: {
    type: BlockTypeEnum.HTTP,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.HTTP],
        authToken: null,
        url: '',
        headers: [],
      },
    },
  },
  [BlockTypeEnum.CREATE_RENEWAL]: {
    type: BlockTypeEnum.CREATE_RENEWAL,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.CREATE_RENEWAL],
        assignees: [],
        targetTimeValue: DEFAULT_BLOCK_TARGET_TIME_VALUE,
        shouldCompleteAutomatically: false,
        notificationMessage: defaultTaskNotificationMessage,
        tasks: [],
        workflowConfig: {
          renewalAutoStartWorkflow: false,
          renewalWorkflow: {
            id: '',
            title: '',
          },
          renewalAssessmentNotice: 90,
          renewalInitialWorkflowAssignee: {
            id: '',
            type: AssigneeTypeEnum.VARIABLE,
            value: '',
          },
        },
      },
    },
  },
  [BlockTypeEnum.COMMIT_SUPPLIER_DATA]: {
    type: BlockTypeEnum.COMMIT_SUPPLIER_DATA,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.COMMIT_SUPPLIER_DATA],
        assignees: [],
        tasks: [{ type: RequestStepTaskTypeEnum.ACCOUNTING_SUPPLIER_DATA_SYNC }],
        targetTimeValue: DEFAULT_BLOCK_TARGET_TIME_VALUE,
        notificationMessage: defaultCommitSupplierDataNotificationMessage,
        syncWithAccountingIntegration: true,
      },
    },
  },
  [BlockTypeEnum.CREATE_AGREEMENT]: {
    type: BlockTypeEnum.CREATE_AGREEMENT,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.CREATE_AGREEMENT],
        assignees: [],
        tasks: [],
        targetTimeValue: DEFAULT_BLOCK_TARGET_TIME_VALUE,
        notificationMessage: defaultCreateAgreementNotificationMessage,
        shouldCompleteAutomatically: false,
      },
    },
  },
  [BlockTypeEnum.CANCEL_AGREEMENT]: {
    type: BlockTypeEnum.CANCEL_AGREEMENT,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.CANCEL_AGREEMENT],
        assignees: [],
        tasks: [],
        targetTimeValue: DEFAULT_BLOCK_TARGET_TIME_VALUE,
        notificationMessage: defaultCancelAgreementNotificationMessage,
        shouldCompleteAutomatically: false,
      },
    },
  },
  [BlockTypeEnum.CANCEL_ASSESSMENTS]: {
    type: BlockTypeEnum.CANCEL_ASSESSMENTS,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.CANCEL_ASSESSMENTS],
        shouldCompleteAutomatically: true,
        assignees: [],
        tasks: [],
        targetTimeValue: DEFAULT_BLOCK_TARGET_TIME_VALUE,
        notificationMessage: defaultCancelAssessmentsManualNotificationMessage,
      },
    },
  },
  [BlockTypeEnum.CREATE_PO]: {
    type: BlockTypeEnum.CREATE_PO,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.CREATE_PO],
        assignees: [],
        tasks: [],
        targetTimeValue: DEFAULT_BLOCK_TARGET_TIME_VALUE,
        notificationMessage: defaultCreatePONotificationMessage,
        syncWithAccountingIntegration: true,
      },
    },
  },
  [BlockTypeEnum.RFX]: {
    type: BlockTypeEnum.RFX,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.RFX],
        assignees: [requestorAssignee],
        defaultFormTemplateIds: [],
        notificationMessage: defaultRfxNotificationMessage,
        targetTimeValue: DEFAULT_BLOCK_TARGET_TIME_VALUE,
        requiresConfidentialityAgreement: false,
        prefillFromSupplierRecord: true,
        prefillFromSupplierRecordOnSupplierProductMatch: false,
        supplierAttachments: [],
      },
    },
  },
  [BlockTypeEnum.AUCTION]: {
    type: BlockTypeEnum.AUCTION,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.AUCTION],
        assignees: [requestorAssignee],
        ...defaultSharedAuctionSettings,
        targetTimeValue: DEFAULT_BLOCK_TARGET_TIME_VALUE,
      },
    },
  },
  [BlockTypeEnum.CREATE_ASSESSMENT]: {
    type: BlockTypeEnum.CREATE_ASSESSMENT,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.CREATE_ASSESSMENT],
        assignees: [],
        tasks: [],
        targetTimeValue: DEFAULT_BLOCK_TARGET_TIME_VALUE,
        notificationMessage: defaultCreateAssessmentNotificationMessage,
        shouldCompleteAutomatically: false,
        workflowId: '',
        assessmentAssignees: [],
        frequency: '',
        shouldUseExactTargetStartDate: false,
        exactTargetStartDate: '',
        relativeTargetStartDate: '',
        autoStartEnabled: false,
      },
    },
  },
  [BlockTypeEnum.PRICE_BENCHMARK]: {
    type: BlockTypeEnum.PRICE_BENCHMARK,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.PRICE_BENCHMARK],
        assignees: [],
        targetTimeValue: DEFAULT_BLOCK_TARGET_TIME_VALUE,
      },
    },
  },
  [BlockTypeEnum.DATA_TRANSFORMATION]: {
    type: BlockTypeEnum.DATA_TRANSFORMATION,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.DATA_TRANSFORMATION],
        outputs: [],
      },
    },
  },
  [BlockTypeEnum.WAITING]: {
    type: BlockTypeEnum.WAITING,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.WAITING],
        condition: {
          type: 'SINGLE' as const,
          key: 0,
          primaryField: null,
          operator: null,
          secondaryField: null,
        },
      },
    },
  },
  [BlockTypeEnum.SUPPLIER_ONLINE_RESOURCES]: {
    type: BlockTypeEnum.SUPPLIER_ONLINE_RESOURCES,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.SUPPLIER_ONLINE_RESOURCES],
        assignees: [],
        targetTimeValue: 1, // 1 hour default
      },
    },
  },
  [BlockTypeEnum.INHERENT_RISK_ASSESSMENT]: {
    type: BlockTypeEnum.INHERENT_RISK_ASSESSMENT,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.INHERENT_RISK_ASSESSMENT],
        description: '',
        assignees: [],
        targetTimeValue: DEFAULT_BLOCK_TARGET_TIME_VALUE,
        notificationMessage: defaultInherentRiskAssessmentNotificationMessage,
        tasks: [],
        prefillFromSupplierRecord: false,
        prefillFromSupplierRecordOnSupplierProductMatch: false,
        agentConfig: { fillIn: DEFAULT_INHERENT_RISK_AGENT_CONFIG },
      },
    },
  },
  [BlockTypeEnum.RISK_CONTROLS_AGENT]: {
    type: BlockTypeEnum.RISK_CONTROLS_AGENT,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.RISK_CONTROLS_AGENT],
        description: '',
        assignees: [],
        targetTimeValue: DEFAULT_BLOCK_TARGET_TIME_VALUE,
        notificationMessage: defaultTaskNotificationMessage,
        tasks: [],
        prefillFromSupplierRecord: false,
        prefillFromSupplierRecordOnSupplierProductMatch: false,
        agentConfig: { fillIn: RISK_CONTROLS_AGENT_FILL_IN_CONFIG },
      },
    },
  },
  [BlockTypeEnum.NOTIFY_SLACK]: {
    type: BlockTypeEnum.NOTIFY_SLACK,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.NOTIFY_SLACK],
        channelId: '',
        channelName: '',
        notificationMessage: defaultChannelNotificationMessage,
      },
    },
  },
  [BlockTypeEnum.NOTIFY_TEAMS]: {
    type: BlockTypeEnum.NOTIFY_TEAMS,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.NOTIFY_TEAMS],
        channelId: '',
        channelName: '',
        notificationMessage: defaultChannelNotificationMessage,
      },
    },
  },
  [BlockTypeEnum.REJECT_REQUEST]: {
    type: BlockTypeEnum.REJECT_REQUEST,
    config: {
      payload: {
        label: BlockDefaultLabel[BlockTypeEnum.REJECT_REQUEST],
        notificationMessage: defaultRejectRequestNotificationMessage,
      },
    },
  },
}

export const getNodeNavigation = <T extends keyof DefaultConfig>(
  type: T,
  t: TFunction
): {
  name: string
  defaultConfig: DefaultConfig[T]
  type: T
} => {
  return {
    name: t(BlockDefaultLabel[type]),
    defaultConfig: translateBlockConfig(BlockDefaultConfig[type], t),
    type,
  }
}

// HACK: Translates the label of a block config
const translateBlockConfig = <T extends keyof DefaultConfig>(config: DefaultConfig[T], t: TFunction): DefaultConfig[T] => {
  return {
    ...config,
    config: {
      ...config.config,
      payload: {
        ...config.config.payload,
        label: config.config.payload.label ? t(config.config.payload.label) : config.config.payload.label,
      },
    },
  }
}

export type { NodeCustomAttributes }

export type NodeMetadata = {
  type: string
  config: {
    payload: {
      label: string
      tasks?: { formTemplateId: string; type: RequestStepTaskTypeEnum }[]
      assignees?: AssigneeEntity[]
      url?: string
      description?: string
    }
  }
} & NodeCustomAttributes<BlockConfig>

export const nodeMetadataIsOfType = <T extends BlockTypeEnum>(type: T) => {
  return <M extends NodeCustomAttributes<BlockConfig>>(metadata: M): metadata is M & { type: T; config: BlockConfig<T> } => {
    return metadata.type === type
  }
}

// Milestone and data transformation blocks are never shown on the timeline, regardless of config.
export const isBlockHiddenFromTimeline = <M extends NodeCustomAttributes<BlockConfig>>(metadata: M): boolean => {
  if (metadata.type === BlockTypeEnum.MILESTONE || metadata.type === BlockTypeEnum.DATA_TRANSFORMATION) {
    return true
  }

  return Boolean(nodeMetadataIsOfType(BlockTypeEnum.EXTERNAL_OPERATION)(metadata) && metadata.config.payload.hideFromTimeline)
}

export type NodeModel = {
  x?: number
  y?: number
  type: 'common' | 'milestone' | 'diagram-nodes'
  selected: boolean
  extras: object
  id: string
  locked: boolean
  metadata: NodeMetadata
  ports: {
    x?: number
    y?: number
    id: string
    type: string
    alignment: 'left' | 'right'
    name: string
    links: string[]
    parentNode?: string
  }[]
}

export type LinkModel = {
  type: 'common-link'
  color: string
  curvyness: number
  id: string
  labels: Array<any>
  locked: boolean
  points: Array<any>
  selected: boolean
  selectedColor: string
  source?: string
  sourcePort?: string
  target?: string
  targetPort?: string
  width: number
  extras: any
}

export type WorkflowCanvasModel = {
  offsetX: number
  offsetY: number
  zoom: number
  gridSize: number
  layers: {
    isSvg: boolean
    transformed: boolean
    models: {
      [x: string]: NodeModel | LinkModel
    }
    type: string
    selected: boolean
    extras: object
    id: string
    locked: boolean
  }[]
  id: string
  locked: boolean
}
export type CanvasModel = WorkflowCanvasModel

export type ViewModelWorkflowNode = {
  id: string
  type?: string
  data: Omit<NodeMetadata, 'type'> & { blockType: BlockTypeEnum }
}

export type ViewModelStickyNoteNode = {
  id: string
  type: 'stickyNote'
  data: { text: string }
}

export type ViewModelNode = ViewModelWorkflowNode | ViewModelStickyNoteNode

export type ViewModel = {
  nodes: Array<ViewModelNode>
  edges: Array<{
    id: string
    source: string
    target: string
  }>
  viewport: {
    x: number
    y: number
    zoom: number
  }
}

export type BlockWithAssignees =
  | ApprovalBlockConfig
  | EngagementBlockConfig
  | NotificationBlockConfig
  | RenewalAssessmentBlockConfig
  | SupplierOnboardingBlockConfig

export type EmailDeliveryError = {
  /** @description the user who did not receive the notification  */
  deliveryFailedUser: {
    id: string
    email: string
  }
  /** @description Describes whether a user has actioned a failed email notification (any request step assignee action)  */
  resolved: boolean
}
